<template>
  <div class="app-shell" :class="appStore.theme">
    <button v-if="soundNeedsUnlock" class="sound-unlock" type="button" @click="enableSound">
      <span>🔊</span>
      {{ appStore.language === 'el' ? 'Πατήστε για ενεργοποίηση ήχου' : 'Tap to enable sound' }}
    </button>
    <div v-if="closingSeconds !== null" class="room-closing-banner">
      {{ t('room.closing', { seconds: closingSeconds }) }}
    </div>
    <div v-else-if="gameExitSeconds !== null" class="room-closing-banner game-exit-banner">
      {{ t('room.game_closing', { seconds: gameExitSeconds }) }}
    </div>
    <!-- Shows for a couple of seconds before initialization -->
    <SplashScreen v-if="appStore.viewState=='created'" />

    <!-- Respect init values and loads db -->
    <LoadingPage
      v-else-if="appStore.viewState === 'loading'"
      :theme="appStore.theme"
      :language="appStore.language"
      :user="appStore.creator"
      :environment="appStore.environment"/>

    <!-- Shows only if game is not selected -->
    <IdlePage v-else-if="['idle', 'join'].includes(appStore.viewState)" />

    <!-- Shows when a universal game is selected -->
    <GamePage v-else-if="appStore.viewState === 'game'" />

    <!-- Shows when a custom game is selected -->
    <CounterPage v-else-if="appStore.viewState === 'counter'" />

    <component v-else-if="appStore.viewState === 'playing'" :is="activeGamePage" />

    <!-- Console is shown for developer-->
    <ConsoleOverlay v-if="consoleState" />

  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/appStore'
import eventBus from './plugins/eventBus'
import { useWebsocketStore } from '@/stores/websocketStore'
import { soundManager, SOUNDS } from '@/services/soundManager'

import SplashScreen from './pages/SplashScreen.vue'
import LoadingPage from './pages/LoadingPage.vue'
import IdlePage from './pages/IdlePage.vue'
import GamePage from './pages/GamePage.vue'
import CounterPage from './pages/CounterPage.vue'
import TicTacToePage from './pages/TicTacToePage.vue'
import FourInARowPage from './pages/FourInARowPage.vue'
import QuizPage from './pages/QuizPage.vue'
import FlagsQuizPage from './pages/FlagsQuizPage.vue'
import GoPage from './pages/GoPage.vue'
import ClosestWinsPage from './pages/ClosestWinsPage.vue'
import MathRushPage from './pages/MathRushPage.vue'
import KeepItUpPage from './pages/KeepItUpPage.vue'
import FlashbackPage from './pages/FlashbackPage.vue'
import TickTockBombPage from './pages/TickTockBombPage.vue'
import ConsoleOverlay from './pages/ConsoleOverlay.vue'

const socketStore = useWebsocketStore()

const appStore = useAppStore()
const soundNeedsUnlock = ref(false)
const checkAlive = ref(null)
const consoleState = ref(false)
const isCastReceiver = /AndroidTV|CrKey/i.test(navigator.userAgent)
const closingSeconds = ref(null)
let closingTimer = null
const gameExitSeconds = ref(null)
let gameExitTimer = null
let gameExitDeadline = null
let gameExitGameId = null
let gameExitToken = null
let logForwardingInstalled = false
const { t, locale } = useI18n()
const activeGamePage = computed(() => {
  const game = appStore.room?.game || appStore.game || {}
  const mechanic = String(game.mechanic || '').trim().toLowerCase()
  const receiverPage = String(game.receiverPage || '').trim().toLowerCase()
  const controllerType = String(game.gameplay?.controller?.type || '').trim().toLowerCase()
  const gameIdentity = [game.id, game.slug, game.name?.en, game.name?.el, game.name]
    .filter(value => typeof value === 'string')
    .join(' ')
    .toLowerCase()
  const datasetId = String(game.dataTableId?.value || game.dataTableId?.id || game.dataTableId || '').trim().toLowerCase()
  if (receiverPage === 'tick-tock-bomb' || gameIdentity.includes('tick tock bomb')) {
    return TickTockBombPage
  }
  if (receiverPage === 'flashback' || mechanic === 'memory' || gameIdentity.includes('flashback')) {
    return FlashbackPage
  }
  if (receiverPage === 'flags-quiz' || (game.contentSource === 'data' && (datasetId.includes('flag') || gameIdentity.includes('flag')))) {
    return FlagsQuizPage
  }
  if (mechanic === 'math' || gameIdentity.includes('math rush')) {
    return MathRushPage
  }
  if (mechanic === 'estimation' || gameIdentity.includes('closest wins') || (controllerType === 'number-input' && game.contentSource === 'data')) {
    return ClosestWinsPage
  }
  if (receiverPage === 'keep-it-up' || mechanic === 'arcade' || gameIdentity.includes('keep it up')) {
    return KeepItUpPage
  }
  if (mechanic === 'reflex' || controllerType === 'single-action-button') {
    return GoPage
  }
  if (game.mechanic === 'quiz' || controllerType === 'choice-buttons') {
    return QuizPage
  }
  if (game.id === 'd9e6ac0f-0650-4ca7-8ca0-2d4b7eddc0ff' || controllerType === 'column-picker') {
    return FourInARowPage
  }
  return TicTacToePage
})

watch(() => appStore.language, (language) => {
  locale.value = language === 'el' ? 'el' : 'en'
}, { immediate: true })

watch(() => appStore.theme, (theme) => {
  const light = theme === 'light'
  const background = light ? '#fff' : '#000'
  const foreground = light ? '#000' : '#fff'
  document.documentElement.style.backgroundColor = background
  document.body.style.backgroundColor = background
  document.body.style.color = foreground
}, { immediate: true })

onMounted(() => {
  window.addEventListener('smeegoon-audio-blocked', handleAudioBlocked)
  soundManager.play(SOUNDS.startup)
  initializeReceivers()
  installLogForwarding()
  initializeDebugShortcuts()
  //initializeCheckAliveTimeout()
  if(process.env.NODE_ENV === 'development'){
    console.log('make console visible')
    //consoleState.value = true
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('smeegoon-audio-blocked', handleAudioBlocked)
  soundManager.stopAll()
  terminateReceivers()
  terminateDebugShortcuts()
})

function handleAudioBlocked () {
  soundNeedsUnlock.value = true
}

async function enableSound () {
  soundNeedsUnlock.value = !(await soundManager.unlock())
}

function initializeDebugShortcuts() {
  eventBus.on('debug-key', (data) => {
    console.log('Debug key received in App.vue:', data)
    
    if (data.type === 'number') {
      switch(data.value) {
        case 1: 
          appStore.viewState = 'idle'
          appStore.status = 'idle'
          break
        case 2: 
          appStore.viewState = 'join'
          appStore.status = 'launched'
          break
        case 3: 
          appStore.viewState = 'game'
          appStore.status = 'playing'
          break
        case 4: 
          appStore.viewState = 'counter'
          appStore.status = 'playing'
          break
        case 0: 
          appStore.viewState = 'loading'
          appStore.status = 'initialized'
          break
      }
    }

    if (data.type === 'action') {
      if (data.value === 'enter') {
        // Toggle player 1
        if (appStore.players.includes('debug-player-1')) {
          appStore.leaveGame({ userId: 'debug-player-1' })
        } else {
          appStore.joinGame({ userId: 'debug-player-1' })
        }
      }
      if (data.value === 'back') {
         // Toggle player 2
         if (appStore.players.includes('debug-player-2')) {
          appStore.leaveGame({ userId: 'debug-player-2' })
        } else {
          appStore.joinGame({ userId: 'debug-player-2' })
        }
      }
    }
  })
}

function terminateDebugShortcuts() {
  eventBus.off('debug-key')
}

function initializeReceivers() {
  eventBus.on('sync', (data) => {
    console.log('[App] Syncing room state:', data)
    if (data.room) {
      // Update global store with the full room object
      appStore.setRoom(data.room)
      // The Worker room remains authoritative after refreshes, reconnects,
      // or a missed Cast initialization message.
      if (data.room.creator) appStore.creator = data.room.creator
      if (data.room.language) appStore.language = data.room.language === 'el' ? 'el' : 'en'
      if (data.room.theme) appStore.theme = data.room.theme === 'light' ? 'light' : 'dark'
      if (data.room.environment) appStore.environment = data.room.environment
      updateGameExitCountdown(
        data.room.gameAbandonDeadline,
        data.room.gameAbandonGameId || data.room.game?.id,
        data.room.gameAbandonToken
      )

      // If we are in SplashScreen (created state) but the room already has a creator,
      // it means we missed the 'init' event or just refreshed. Let's move to Loading.
      if (appStore.viewState === 'created' && data.room.creator) {
        console.log('[App] Room already has a creator, moving to loading state')
        appStore.initApp({
          parameters: {
            theme: data.room.theme || 'dark',
            language: data.room.language || 'en',
            environment: data.room.environment || 'development'
          },
          player: { id: data.room.creator },
          game: data.room.game
        })
      }

      // Update players list
      if (data.room.players) {
        if (data.room.players.length > 0) stopClosingCountdown()
        // If a new player joined that we didn't know about
        data.room.players.forEach(player => {
          if (!appStore.players.includes(player.id)) {
            console.log(`[App] New player detected via sync: ${player.id}`)
            appStore.players.push(player.id)
            eventBus.emit('player-joined', { userId: player.id })
          }
        })
        
        // Handle removals
        appStore.players = appStore.players.filter(pid => 
          data.room.players.some(p => p.id === pid)
        )
      }
      
      // Update game/status if needed
      if (data.room.status && data.room.status !== appStore.status) {
        console.log(`[App] Status changed via sync: ${appStore.status} -> ${data.room.status}`)
        
        // Handle transitions
        if (data.room.status === 'launched' && appStore.viewState !== 'join') {
          appStore.launchGame({
            game: data.room.game,
            room: data.room,
            userId: data.room.gameStartedByUserId
          })
        } else if (['starting', 'playing'].includes(data.room.status)) {
          // A receiver can reconnect after the start event was sent. Restore
          // the game directly from the canonical room state in that case.
          appStore.launchGame({
            game: data.room.game,
            room: data.room,
            userId: data.room.gameStartedByUserId
          })
          appStore.startGame({ userId: data.room.gameStartedByUserId })
        } else if (data.room.status === 'idle' && appStore.viewState !== 'idle') {
          appStore.idle({})
        }
        
        appStore.status = data.room.status
      }

      // Status and view state can briefly get out of step after reconnects or
      // multiple players leaving together. The room is authoritative: every
      // idle sync must close any stale game page, even when the local status
      // already happens to say "idle".
      if (data.room.status === 'idle' && appStore.viewState !== 'idle') {
        appStore.idle({})
        appStore.status = 'idle'
      }

      // A room with no players is never allowed to retain a game screen. A
      // browser receiver advertises its six-digit code again; a Chromecast
      // has no code-based join flow and therefore remains on its idle page.
      if (data.room.players?.length === 0 && ['created', 'initialized', 'idle'].includes(data.room.status)) {
        appStore.game = { id: '', players: [] }
        if (isCastReceiver) {
          appStore.idle({})
        } else if (appStore.viewState !== 'created') {
          resetToCodeScreen()
        }
      }
    }
  })

  eventBus.on('init', ({parameters, player}) => {
    console.log('initializing...')
    appStore.initApp({parameters, player})
    if (!appStore.players.includes(player.id)) {
      appStore.players.push(player.id);
    }
    if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) {
      socketStore.socket.send(JSON.stringify({
        event: 'initialized',
        data: {
          creator: appStore.creator,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
  })

  eventBus.on('launch', (data) => {
    if(appStore.status!='launched'){
      console.log('launching')
      appStore.launchGame({
        game:data.game,
        room :data.room,
        userId: data.userId
      });
      if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) { 
        socketStore.socket.send(JSON.stringify({
          event: 'launched',
          data: {
            game:data.game,
            room :data.room,
            userId: data.userId
          }
        }));
      }
    }
  })

   eventBus.on('idle', (data) => {
    console.log('launching')
    appStore.idle({});
  })

  // this is called if init occurs with specific game id
  eventBus.on('game-launched', (data) => {
    if(appStore.status!='launched'){
      console.log('launching from init')
      appStore.launchGame({
        userId :data.userId, 
        game: data.game, 
        environment: data.environment
      });
      if (!appStore.players.includes(data.userId)) {
        appStore.players.push(data.userId);
      }
      if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) { 
        socketStore.socket.send(JSON.stringify({
          event: 'launched',
          data: {
            gameId: appStore.gameId,
            creator: appStore.user,
            environment: appStore.environment,
            players:appStore.players
          }
        }));
      }
    }
  })

  eventBus.on('join', (data) => {
    console.log('joining')
    soundManager.play(SOUNDS.playerJoined)
    appStore.joinGame({
      userId :data.userId, 
      environment: data.environment
    });
    if (!appStore.players.includes(data.userId)) {
      appStore.players.push(data.userId);
    }
    eventBus.emit('player-joined', data)
    if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) { 
      socketStore.socket.send(JSON.stringify({
        event: 'joined',
        data: {
          gameId: appStore.gameId,
          creator: appStore.user,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
    if (appStore.players.length>=appStore.game.minPlayers) {
      socketStore.socket.send(JSON.stringify({
        event: 'ready',
        data: {
          gameId: appStore.gameId,
          creator: appStore.user,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
  })

  eventBus.on('left', (data) => {
    appStore.leaveGame({
      userId :data.userId, 
      environment: data.environment
    });
    eventBus.emit('player-left', data)
    if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) { 
      socketStore.socket.send(JSON.stringify({
        event: 'left',
        data: {
          gameId: appStore.gameId,
          creator: appStore.user,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
    if(appStore.status=='idle'){
      if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) { 
      socketStore.socket.send(JSON.stringify({
        event: 'cancelled',
        data: {
          gameId: data.gameId,
          creator: appStore.user,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
    }
  })

  eventBus.on('reset', () => {
    resetToCodeScreen()
    // A reset means the room session ended (for example, every player left).
    // Preserve the code during browser refreshes, but retire it at the end of
    // a session so the receiver advertises a new room next time.
    const retiredRoomCode = localStorage.getItem('smeegoon-receiver-room-code')
    localStorage.removeItem('smeegoon-receiver-room-code')
    if (retiredRoomCode) localStorage.removeItem(`smeegoon-receiver-capability:${retiredRoomCode}`)
    window.location.reload()
  })

  eventBus.on('receiver-identity-conflict', ({ name } = {}) => {
    // A six-digit code may eventually collide with a previously claimed code.
    // Retire it and let startup choose a fresh code instead of taking over the
    // other receiver's identity.
    localStorage.removeItem('smeegoon-receiver-room-code')
    if (name) localStorage.removeItem(`smeegoon-receiver-capability:${name}`)
    window.location.reload()
  })

  eventBus.on('worker-connection-timeout', () => {
    resetToCodeScreen()
  })

  eventBus.on('room-closing', ({ seconds = 30 } = {}) => {
    startClosingCountdown(seconds)
  })

  eventBus.on('game-closing', ({ seconds = 5, deadline, gameId, token } = {}) => {
    updateGameExitCountdown(deadline || (Date.now() + (seconds * 1000)), gameId, token)
  })

  eventBus.on('restart', ({ room } = {}) => {
    if (room) appStore.setRoom(room)
    appStore.status = 'playing'
    appStore.viewState = 'playing'
    eventBus.emit('restart-round')
  })

  eventBus.on('admin-toggle-log-overlay', ({ enabled } = {}) => {
    consoleState.value = typeof enabled === 'boolean' ? enabled : !consoleState.value
    console.info(`[Admin] Log overlay ${consoleState.value ? 'enabled' : 'disabled'}`)
  })

  eventBus.on('admin-get-logs', () => {
    console.info('[Admin] Requested receiver logs')
  })

  eventBus.on('cast-room-ready', () => {
    if (appStore.viewState === 'created') {
      appStore.idle({})
    }
  })

  eventBus.on('cast-config', ({ userId, language, theme, environment } = {}) => {
    // Cast sends this message for every sender that attaches to the session.
    // Only the first sender may configure a new room. Once the Worker has
    // assigned its creator, the canonical creator preferences remain locked
    // for the lifetime of that room; later players must not cause a theme or
    // language flash before the next room sync restores the correct values.
    if (appStore.room?.creator) {
      console.info(`[Cast] Ignoring configuration from ${userId || 'unknown sender'}; room configuration belongs to ${appStore.room.creator}`)
      return
    }
    if (userId) appStore.creator = userId
    if (language) appStore.language = language === 'el' ? 'el' : 'en'
    if (theme) appStore.theme = theme === 'light' ? 'light' : 'dark'
    if (environment) appStore.environment = environment
  })

  eventBus.on('start', (data) => {
    console.log('starting')
    appStore.startGame({
      userId :data.userId, 
      environment: data.environment
    });
    // The game page repeats this after it mounts, but do not leave the room
    // in `starting` if that component mount acknowledgement is missed.
    socketStore.send({ event: 'playing', data: { gameId: appStore.game?.id } })
  })
}

function serializeLogArgument(arg) {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) {
    return JSON.stringify({ name: arg.name, message: arg.message, stack: arg.stack || null })
  }
  if (typeof Event !== 'undefined' && arg instanceof Event) {
    return JSON.stringify({
      type: arg.type,
      message: arg.message || null,
      readyState: arg.target?.readyState ?? null,
      url: arg.target?.url || null
    })
  }
  try { return JSON.stringify(arg) } catch { return String(arg) }
}

function installLogForwarding() {
  if (logForwardingInstalled) return
  logForwardingInstalled = true
  for (const method of ['log', 'info', 'warn', 'error']) {
    const original = console[method].bind(console)
    console[method] = (...args) => {
      original(...args)
      const line = args.map(serializeLogArgument).join(' ')
      if (line && socketStore.socket?.readyState === WebSocket.OPEN) {
        socketStore.socket.send(JSON.stringify({ event: 'receiver-log', data: { line: `[${method}] ${line}` } }))
      }
    }
  }
}

function resetToCodeScreen() {
  console.log('[App] Resetting to code screen')
  stopClosingCountdown()
  appStore.viewState = 'created'
  appStore.status = 'initialized'
  appStore.players = []
  appStore.room = { players: [], status: '', creator: '', game: {} }
  appStore.game = { id: '', players: [] }
}

function startClosingCountdown(seconds) {
  stopClosingCountdown()
  closingSeconds.value = Math.max(0, Number(seconds) || 30)
  closingTimer = setInterval(() => {
    closingSeconds.value = Math.max(0, closingSeconds.value - 1)
    if (closingSeconds.value === 0) stopClosingCountdown()
  }, 1000)
}

function stopClosingCountdown() {
  clearInterval(closingTimer)
  closingTimer = null
  closingSeconds.value = null
}

function updateGameExitCountdown(deadline, gameId, token) {
  const numericDeadline = Number(deadline)
  if (!Number.isFinite(numericDeadline) || numericDeadline <= Date.now()) {
    stopGameExitCountdown()
    return
  }
  if (gameExitDeadline === numericDeadline && gameExitGameId === gameId && gameExitToken === token && gameExitTimer) return

  stopGameExitCountdown()
  gameExitDeadline = numericDeadline
  gameExitGameId = gameId || null
  gameExitToken = token || null
  const update = () => {
    const remaining = Math.max(0, Math.ceil((gameExitDeadline - Date.now()) / 1000))
    gameExitSeconds.value = remaining
    if (remaining === 0) {
      // The Worker is authoritative, but render the safe local transition too.
      // The game id + token checks prevent an old countdown from closing a
      // newer game while its final sync is in flight.
      const room = appStore.room || {}
      const isCurrentCountdown = Boolean(
        gameExitGameId && gameExitToken &&
        room.game?.id === gameExitGameId &&
        room.gameAbandonGameId === gameExitGameId &&
        room.gameAbandonToken === gameExitToken
      )
      clearInterval(gameExitTimer)
      gameExitTimer = null
      if (isCurrentCountdown) {
        appStore.idle({})
        appStore.status = 'idle'
        appStore.game = { id: '', players: [] }
        gameExitSeconds.value = null
      }
    }
  }
  update()
  gameExitTimer = setInterval(update, 250)
}

function stopGameExitCountdown() {
  clearInterval(gameExitTimer)
  gameExitTimer = null
  gameExitDeadline = null
  gameExitGameId = null
  gameExitToken = null
  gameExitSeconds.value = null
}

/*function initializeCheckAliveTimeout(){
  checkAlive.value = setInterval(()=>{
    if (socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) {
    socketStore.socket.send(JSON.stringify({
      event: 'report',
      data: {
        name: appStore.name
      }
    }));
  }
  },30000)
}*/

function terminateReceivers() {
  eventBus.off('sync')
  eventBus.off('init')
  eventBus.off('launch')
  eventBus.off('join')
  eventBus.off('left')
  eventBus.off('reset')
  eventBus.off('worker-connection-timeout')
  eventBus.off('room-closing')
  eventBus.off('game-closing')
  eventBus.off('restart')
  eventBus.off('admin-toggle-log-overlay')
  eventBus.off('admin-get-logs')
  eventBus.off('cast-room-ready')
  eventBus.off('cast-config')
  stopClosingCountdown()
  stopGameExitCountdown()
  eventBus.off('game-launched')
  
  // Explicitly disconnect the socket when the receiver unmounts/refreshes
  if (socketStore.isConnected) {
    console.log('[App] Refreshing/Unmounting - closing receiver socket')
    socketStore.disconnect()
  }
}
</script>

<style>
#app {
  font-family: 'Noto Sans', sans-serif;
  text-align: center;
  color: #2c3e50;
}

.app-shell {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.sound-unlock {
  position: fixed;
  z-index: 2000;
  inset: 0;
  width: 100%;
  height: 100%;
  display: grid;
  place-content: center;
  gap: 1rem;
  border: 0;
  background: rgba(5, 7, 12, 0.9);
  color: #fff3a0;
  font: 600 clamp(1.1rem, 3vw, 2rem) 'Noto Sans', sans-serif;
  cursor: pointer;
}

.sound-unlock span {
  font-size: clamp(3rem, 10vw, 7rem);
}

.dark {
  background: #000;
  color: #fff;
}

.light {
  background: #fff;
  color: #000;
}

.room-closing-banner {
  position: fixed;
  z-index: 1000;
  top: 1.25rem;
  right: 1.25rem;
  max-width: min(42rem, 70vw);
  padding: 0.8rem 1.1rem;
  border-radius: 0.65rem;
  background: rgba(30, 15, 10, 0.92);
  border: 1px solid rgba(255, 197, 104, 0.8);
  color: #fff7b2;
  font-family: 'Noto Sans', sans-serif;
  font-size: clamp(0.85rem, 1.5vw, 1.15rem);
  font-weight: 500;
  line-height: 1.35;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.35);
}

.game-exit-banner {
  background: rgba(20, 28, 42, 0.94);
  border-color: rgba(88, 184, 255, 0.85);
  color: #d7efff;
}
</style>
